import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { NoyauAttempts } from '@/components/admin/sessions/walk/walk-noyau-attempts';
import { ExpectedNotePill } from '@/components/admin/sessions/walk/walk-noyau-expected-note-pill';
import {
  type CandidateReadings,
  type EliminationView,
  FUNCTION_BAR_CLASS,
  PASSAGE_SIDE_CLASS,
  type TextPart,
  candidateReadings,
  conflictSentences,
  discardedSentence,
  eliminationOf,
  eliminationSummary,
  failedReadings,
  functionBar,
  functionsSummary,
  levelRows,
  levelsSummary,
  noteRows,
  notesSummary,
  noyauNamed,
  rankedAttempts,
  sharesLine,
  styleAbsentSentence,
  styleReadingsFailureSentence,
  styleReadingsIntro,
  styleSummary,
  thresholdsLine,
  weightSentence,
} from '@/lib/session-analysis';
import { drawSentence } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import {
  ANALYST_LEVEL_LABELS,
  type AnalysisStyleNote,
  type NoyauAnalysis,
  type NoyauCode,
  type NoyauDetermination,
  noyauOf,
  styleReadingsOf,
} from '@matter_extractor/shared';
import { Icon } from '@pms-digital/design-system';
import { Fragment, type ReactNode, useId, useState } from 'react';

/**
 * The REASONING of a determination in two passes, section by section (analyste.md
 * §11.1; maquettes `ParcoursAvantApres` and `ParcoursEtats`): the first pass, the
 * levels, the elimination, the second pass - or the yellow line of a style that
 * could not be read - and the six notes.
 *
 * ## Foldable, as the planches draw them
 *
 * Each section is a button that opens and closes it, and a section that is folded
 * says what it holds in one line (« 6 objectifs lus, trois parts sur 100 chacun »)
 * so the page can be read shut. All five start open - the maquette `ParcoursAvantApres`
 * - except when the style is absent: then the three sections above the yellow
 * line start folded, as `ParcoursEtats` draws them, so the line and the notes it
 * explains are the first thing seen. The state is the block's own, never the
 * URL's: it is a posture of the reader.
 *
 * ## Nothing is derived here
 *
 * Every figure is read off the `analysis` the determination stored, and worded by
 * `lib/session-analysis.ts`; the code that fixed the levels and eliminated the
 * noyaux ran on the server, once, and this screen only says what it decided.
 * The colours of the three functions are the tokens of `styles.css`.
 */

export interface WalkNoyauAnalysisProps {
  readonly determination: NoyauDetermination;
  readonly analysis: NoyauAnalysis;
  /** The mark of one of the six notes: a trial answers marks of its own (`contracts.ts`). */
  readonly scoreTestId: string;
  /** The noyau the NPC was expected to give (#216, lot K): its row takes the pill « attendu » and the pale ground. */
  readonly expectedNoyau?: NoyauCode | null;
  /** What sits right before the six notes: the band « Face à l'attendu » (#216, lot K). */
  readonly beforeNotes?: ReactNode;
}

export function WalkNoyauAnalysis({
  determination,
  analysis,
  scoreTestId,
  expectedNoyau = null,
  beforeNotes = null,
}: WalkNoyauAnalysisProps) {
  const absent = analysis.style?.state === 'absent';
  const elimination = eliminationOf(analysis);
  const rows = noteRows(determination, analysis);
  const notes = analysis.style?.state === 'rendered' ? analysis.style.notes : [];
  const conflicts = conflictSentences(analysis);
  // The style read several times (#229); null at one reading, the default, and the block is then the one of before.
  const readings = styleReadingsOf(analysis);
  const failureLine = styleReadingsFailureSentence(analysis, determination.calls);
  const failedRuns = failedReadings(analysis, determination.calls).map((entry) => entry.run);
  // The readings unfolded by a click: the state of the block, never of the address, several at once.
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set());
  const toggleReading = (key: string) =>
    setUnfolded((now) => {
      const next = new Set(now);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  return (
    <>
      <Section
        id="passe-1"
        title="Passe 1 · les fonctions par objectif"
        summary={functionsSummary(analysis)}
        open={!absent}
      >
        <FunctionsTable analysis={analysis} />
      </Section>

      <Section
        id="niveaux"
        title="Les niveaux"
        summary={<Parts parts={levelsSummary(analysis)} />}
        open={!absent}
      >
        <LevelsTable analysis={analysis} />
        <p className="text-gray-600 text-caption">
          <Parts
            parts={thresholdsLine(analysis)}
            strongClass="font-semibold text-gray-700 tabular-nums"
          />
        </p>
        {conflicts.map((sentence) => (
          <p
            key={sentence}
            data-testid={SESSIONS_TEST_IDS.walkNoyauConflict}
            className="text-gray-700 text-caption"
          >
            {sentence}
          </p>
        ))}
      </Section>

      <Section
        id="elimination"
        title="L’élimination"
        summary={<Parts parts={eliminationSummary(elimination)} />}
        open={!absent}
      >
        <Elimination view={elimination} />
      </Section>

      {absent ? (
        <div className="flex flex-col gap-2.5 border-gray-100 border-t pt-3.5">
          <YellowLine testId={SESSIONS_TEST_IDS.walkNoyauStyleAbsent}>
            {styleAbsentSentence(determination.calls)}
          </YellowLine>
          {/* The cause of each attempt: the second pass's, the first one was read. */}
          <NoyauAttempts
            attempts={rankedAttempts(determination.calls).filter((attempt) => attempt.pass === 2)}
          />
        </div>
      ) : (
        <Section id="passe-2" title="Passe 2 · le style" summary={styleSummary(analysis)} open>
          {readings === null ? null : (
            <p
              data-testid={SESSIONS_TEST_IDS.walkNoyauReadingsIntro}
              className="text-caption text-gray-600 leading-normal"
            >
              {styleReadingsIntro(readings)}
            </p>
          )}
          {failureLine === null ? null : (
            <div className="flex flex-col gap-2.5">
              <YellowLine
                testId={SESSIONS_TEST_IDS.walkNoyauReadingsFailed}
                failed={failedRuns.join(' ')}
              >
                {failureLine}
              </YellowLine>
              {/* The attempts of the readings that failed, with their cause and their fingerprint. */}
              <NoyauAttempts
                attempts={rankedAttempts(determination.calls).filter(
                  (attempt) =>
                    attempt.pass === 2 && attempt.run !== null && failedRuns.includes(attempt.run),
                )}
              />
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {notes.map((note) => (
              <StyleCard
                key={note.noyau}
                note={note}
                readings={candidateReadings(analysis, note)}
                unfolded={unfolded}
                onToggle={toggleReading}
              />
            ))}
          </div>
        </Section>
      )}

      {beforeNotes}

      <Section id="notes" title="Les six notes" summary={notesSummary(rows)} open>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-small">
            <caption className="sr-only">
              {absent
                ? 'Les six notes, de la compatibilité seule, de la meilleure à la moins bonne'
                : 'Les six notes dérivées des deux passes, de la meilleure à la moins bonne'}
            </caption>
            <thead>
              <tr className="border-gray-200 border-b text-left text-gray-700">
                <Th>Noyau</Th>
                <Th className="w-40">Compatibilité</Th>
                <Th className="w-28">Style</Th>
                <Th className="w-38 pr-0">Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const noyau = noyauOf(row.noyau);
                return (
                  <tr
                    key={row.noyau}
                    data-testid={scoreTestId}
                    data-noyau={row.noyau}
                    data-place={row.place ?? undefined}
                    data-eliminated={row.eliminated ? 'true' : undefined}
                    data-expected={row.noyau === expectedNoyau ? 'true' : undefined}
                    className={cn(
                      'border-gray-200 border-b align-top last:border-b-0',
                      row.noyau === expectedNoyau ? 'bg-bleu-nuit-tint' : null,
                    )}
                  >
                    <th scope="row" className="whitespace-nowrap py-2.5 pr-3 text-left font-normal">
                      <span className="inline-flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="size-2.5 rounded-full"
                          style={{ backgroundColor: noyau.color }}
                        />
                        <span className="font-semibold text-gray-900 text-small">{noyau.name}</span>
                        <span className="text-caption text-gray-700">{row.noyau}</span>
                        {row.place === null ? null : (
                          <StatusBadge tone="accent" size="caption">
                            {row.place}
                          </StatusBadge>
                        )}
                        {row.eliminated ? (
                          <StatusBadge tone="neutral" size="caption">
                            éliminé
                          </StatusBadge>
                        ) : null}
                        {row.noyau === expectedNoyau ? <ExpectedNotePill /> : null}
                      </span>
                    </th>
                    <td className="py-2.5 pr-3 font-semibold text-gray-900 text-small tabular-nums">
                      {row.compatibility ?? '-'}
                    </td>
                    <td className="py-2.5 pr-3 text-small tabular-nums">
                      {row.style === null ? (
                        <>
                          <span aria-hidden="true" className="text-gray-600">
                            -
                          </span>
                          <span className="sr-only">
                            {row.eliminated ? 'sans style, éliminé' : 'style absent'}
                          </span>
                        </>
                      ) : (
                        <span className="font-semibold text-gray-900">{row.style}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap py-2.5">
                      <span className="inline-flex items-center gap-2">
                        <b className="w-7 font-bold text-gray-900 text-small tabular-nums">
                          {row.score}
                        </b>
                        <span
                          aria-hidden="true"
                          className="inline-block h-1.5 w-24 overflow-hidden rounded-full bg-gray-100"
                        >
                          <span
                            className="block h-full rounded-full"
                            style={{ width: `${row.score}%`, backgroundColor: noyau.color }}
                          />
                        </span>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {absent ? null : (
          <p className="text-gray-600 text-caption">
            {weightSentence(analysis.settings.styleWeight, readings)}
          </p>
        )}
        {determination.draw === null ? null : (
          <p className="text-caption text-gray-600">{drawSentence(determination.draw)}</p>
        )}
      </Section>
    </>
  );
}

/* ------------------------------------------------------------------ pieces */

/** A run of parts of a sentence: the figures and the names in bold. */
function Parts({
  parts,
  strongClass = 'font-semibold text-gray-900 tabular-nums',
}: {
  parts: readonly TextPart[];
  strongClass?: string;
}) {
  return (
    <>
      {parts.map((part, index) =>
        part.strong ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
          <b key={index} className={strongClass}>
            {part.text}
          </b>
        ) : (
          // biome-ignore lint/suspicious/noArrayIndexKey: the parts of one sentence never move.
          <Fragment key={index}>{part.text}</Fragment>
        ),
      )}
    </>
  );
}

function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th scope="col" className={cn('py-1.5 pr-3 font-normal', className)}>
      {children}
    </th>
  );
}

/**
 * One section of the block: a heading that is a button, and a body - or, folded,
 * the line that says what the body holds. `open` is where it STARTS.
 */
function Section({
  id,
  title,
  summary,
  open,
  children,
}: {
  id: string;
  title: string;
  summary: ReactNode;
  open: boolean;
  children: ReactNode;
}) {
  const [opened, setOpened] = useState(open);
  const headingId = useId();
  const bodyId = useId();
  const heading = (
    <h4 id={headingId}>
      <button
        type="button"
        aria-expanded={opened}
        // Only while the body is there to point at: folded, the line that sums it up stands in.
        aria-controls={opened ? bodyId : undefined}
        data-testid={SESSIONS_TEST_IDS.walkNoyauSectionToggle}
        onClick={() => setOpened((now) => !now)}
        className="inline-flex cursor-pointer items-center gap-1.5 font-semibold text-body text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <Icon name={opened ? 'expand_more' : 'chevron_right'} size={14} />
        {title}
      </button>
    </h4>
  );
  return (
    <section
      aria-labelledby={headingId}
      data-testid={SESSIONS_TEST_IDS.walkNoyauSection}
      data-section={id}
      data-open={opened ? 'true' : 'false'}
      className={cn(
        'border-gray-100 border-t',
        opened
          ? 'flex flex-col gap-2.5 pt-3.5'
          : 'flex flex-wrap items-center gap-x-4 gap-y-1 pt-3',
      )}
    >
      {heading}
      {opened ? (
        <div id={bodyId} className="flex flex-col gap-2.5">
          {children}
        </div>
      ) : (
        <p className="text-caption text-gray-600">{summary}</p>
      )}
    </section>
  );
}

/** The first pass: one row per objective, its level, the bar of its three parts, its sentence. */
function FunctionsTable({ analysis }: { analysis: NoyauAnalysis }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-caption">
        <caption className="sr-only">
          Les parts du Souverain, du Guerrier et du Producteur sur 100, objectif par objectif,
          rendues par la passe 1
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-700">
            <Th className="w-55">Objectif</Th>
            <Th className="w-72">Parts</Th>
            <Th className="pr-0">Justification</Th>
          </tr>
        </thead>
        <tbody>
          {analysis.functions.map((row) => (
            <tr
              key={row.objectiveId}
              data-testid={SESSIONS_TEST_IDS.walkNoyauFunction}
              data-objective-id={row.objectiveId}
              data-level={row.level}
              className="border-gray-100 border-b align-top last:border-b-0"
            >
              <th scope="row" className="whitespace-nowrap py-2.5 pr-3 text-left font-normal">
                <span className="inline-flex items-center gap-2">
                  <span className="font-semibold text-gray-900 text-small">{row.name}</span>
                  <span className="inline-flex items-center rounded-full bg-bleu-nuit-tint px-2 font-semibold text-bleu-nuit text-micro leading-[18px]">
                    {ANALYST_LEVEL_LABELS[row.level]}
                  </span>
                </span>
              </th>
              <td className="py-2.5 pr-3">
                <span
                  aria-hidden="true"
                  className="flex h-[18px] w-65 max-w-full overflow-hidden whitespace-nowrap rounded-sm font-bold text-micro text-white leading-[18px]"
                >
                  {functionBar(row.shares).map((segment) => (
                    <span
                      key={segment.fn}
                      style={{ width: `${segment.share}%` }}
                      className={cn('overflow-hidden text-center', FUNCTION_BAR_CLASS[segment.fn])}
                    >
                      {segment.label}
                    </span>
                  ))}
                </span>
                <span className="mt-0.5 block text-gray-600 text-caption tabular-nums">
                  {sharesLine(row.shares)}
                </span>
              </td>
              <td className="py-2.5 text-gray-700">{row.justification}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The three levels: the function each is fixed on, the state and what decided it, the parts the code read. */
function LevelsTable({ analysis }: { analysis: NoyauAnalysis }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-caption">
        <caption className="sr-only">
          Les trois niveaux, fixés ou non par le code à partir des parts de la passe 1
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-700">
            <Th className="w-55">Niveau</Th>
            <Th className="w-42">Fonction</Th>
            <Th className="w-70">État</Th>
            <Th className="pr-0">Parts lues</Th>
          </tr>
        </thead>
        <tbody>
          {levelRows(analysis).map((row) => (
            <tr
              key={row.level}
              data-testid={SESSIONS_TEST_IDS.walkNoyauLevel}
              data-level={row.level}
              data-state={row.state}
              className="border-gray-100 border-b align-middle last:border-b-0"
            >
              <th
                scope="row"
                className="py-2.5 pr-3 text-left font-semibold text-gray-900 text-small"
              >
                {row.levelLabel}
              </th>
              <td className="py-2.5 pr-3">
                {row.fn === null ? (
                  <>
                    <span aria-hidden="true" className="text-gray-600">
                      -
                    </span>
                    <span className="sr-only">aucune</span>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'inline-flex size-5 items-center justify-center rounded-sm font-bold text-micro text-white leading-none',
                        FUNCTION_BAR_CLASS[row.fn],
                      )}
                    >
                      {row.fn}
                    </span>
                    <span className="font-semibold text-gray-900 text-small">{row.fnName}</span>
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-3">
                <StatusBadge tone={row.tone}>{row.stateLabel}</StatusBadge>
              </td>
              <td className="py-2.5 text-gray-700 tabular-nums">{row.parts}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** The candidates the levels left, then the noyaux they struck out, each with its sentence for a reader who has no eyes for a strike-through. */
function Elimination({ view }: { view: EliminationView }) {
  const listId = useId();
  const count = view.candidates.length;
  return (
    <>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-caption text-gray-700">
        <span>
          {count === 1 ? 'Reste' : 'Restent'}{' '}
          <b className="font-bold text-gray-900 tabular-nums">{count}</b>{' '}
          {count === 1 ? 'candidat' : 'candidats'} :
        </span>
        {view.candidates.map((code, index) => (
          <span
            key={code}
            data-testid={SESSIONS_TEST_IDS.walkNoyauCandidate}
            data-noyau={code}
            className="inline-flex items-center gap-2"
          >
            <span
              aria-hidden="true"
              className="size-2.5 shrink-0 rounded-full"
              style={{ backgroundColor: noyauOf(code).color }}
            />
            <span>
              <b className="font-semibold text-gray-900 text-small">{noyauNamed(code)}</b>
              {index < count - 1 ? ',' : ''}
            </span>
          </span>
        ))}
      </p>
      {view.eliminated.length === 0 ? (
        <p className="text-caption text-gray-600">
          Aucun noyau n’est éliminé : aucun niveau n’est fixé.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-caption text-gray-700">
          <span id={listId}>Éliminés :</span>
          <ul aria-labelledby={listId} className="flex flex-wrap gap-1.5">
            {view.eliminated.map((entry) => (
              <li key={entry.noyau}>
                <s
                  data-testid={SESSIONS_TEST_IDS.walkNoyauEliminated}
                  data-noyau={entry.noyau}
                  className="inline-block rounded-full bg-gray-100 px-2 text-gray-600 text-caption leading-5"
                >
                  {entry.noyau}
                  <span className="sr-only"> : {entry.sentence}</span>
                </s>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/**
 * The yellow line of a second pass that could not be read, whole or in part (§7.5, §11.1): the
 * left rule `jaune-deep` on a `jaune-tint` ground and the warning icon of the package.
 */
function YellowLine({
  testId,
  failed,
  children,
}: {
  testId: string;
  /** The readings that failed, space-separated - a partial failure of a style read several times. */
  failed?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="note"
      data-testid={testId}
      data-failed={failed}
      className="flex items-start gap-2.5 rounded-md border border-gray-200 border-l-4 border-l-jaune-deep bg-jaune-tint px-3.5 py-2.5"
    >
      <span className="inline-flex shrink-0 text-jaune-deep">
        <Icon name="warning" size={18} />
      </span>
      <p className="text-caption text-gray-800 leading-normal">{children}</p>
    </div>
  );
}

/**
 * One candidate of the second pass: its style, its note, the passages that carry it and those that
 * contradict it.
 *
 * Read several times (#229, §11.1; planche `LecturesParcours`), the note in the head is the COMBINED
 * style and a line under it gives the note of each reading; the reading that carries the note is drawn
 * open under its title, with its passages, its sentence and its discarded passages, and the others are
 * FOLDED on a line each - a click unfolds one, whole, and its line keeps its note and loses the count of
 * its quotations, which are there. `unfolded` is the block's state, `onToggle` writes it.
 */
function StyleCard({
  note,
  readings,
  unfolded,
  onToggle,
}: {
  note: AnalysisStyleNote;
  readings: CandidateReadings | null;
  unfolded: ReadonlySet<string>;
  onToggle: (key: string) => void;
}) {
  const noyau = noyauOf(note.noyau);
  const headingId = useId();
  // At several readings the body is the carrying reading's: the combined note keeps its passages whole.
  const shown = readings === null ? note : readings.carrier.note;
  return (
    <article
      aria-labelledby={headingId}
      data-testid={SESSIONS_TEST_IDS.walkNoyauStyle}
      data-noyau={note.noyau}
      className="flex flex-col gap-2.5 rounded-md border border-gray-200 bg-white p-3.5"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          aria-hidden="true"
          className="size-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: noyau.color }}
        />
        <h5 id={headingId} className="font-semibold text-gray-900 text-small">
          {noyau.name}
        </h5>
        <span className="text-gray-600 text-micro">{note.noyau}</span>
        {note.styleName === '' ? null : (
          <span className="text-caption text-gray-600">
            · style <b className="font-semibold text-gray-900">{note.styleName}</b>
          </span>
        )}
        <span className="ml-auto inline-flex items-baseline gap-0.5 tabular-nums">
          <b className="font-bold text-gray-900 text-body-lg leading-6">{note.score}</b>
          <span className="text-gray-600 text-caption">/ 100</span>
        </span>
      </header>
      {readings === null ? null : (
        <p
          data-testid={SESSIONS_TEST_IDS.walkNoyauReadingsLine}
          className="text-caption text-gray-700 tabular-nums"
        >
          <Parts parts={readings.line} strongClass="font-semibold text-gray-900 tabular-nums" />
        </p>
      )}
      {readings === null ? null : (
        <p
          data-testid={SESSIONS_TEST_IDS.walkNoyauReadingCarrier}
          data-run={readings.carrier.run}
          className="text-caption text-gray-700"
        >
          <Parts parts={readings.carrier.title} strongClass="font-semibold text-gray-900" />
        </p>
      )}
      <ReadingBody note={shown} />
      {readings === null || readings.others.length === 0 ? null : (
        <ul className="flex flex-col border-gray-100 border-t pt-1.5">
          {readings.others.map((other) => (
            <ReadingFold
              key={other.run}
              noyau={note.noyau}
              reading={other}
              open={unfolded.has(`${note.noyau}:${other.run}`)}
              onToggle={() => onToggle(`${note.noyau}:${other.run}`)}
            />
          ))}
        </ul>
      )}
    </article>
  );
}

/** What a reading wrote of a candidate: the passages, the sentence and the passages set aside. */
function ReadingBody({ note }: { note: AnalysisStyleNote }) {
  const discarded = discardedSentence(note.discardedCount);
  return (
    <>
      <Passages label="Soutiennent" side="supporting" passages={note.supporting} />
      <Passages label="Contredisent" side="contradicting" passages={note.contradicting} />
      <p className="text-caption text-gray-700 leading-normal">{note.justification}</p>
      {discarded === null ? null : (
        <p
          data-testid={SESSIONS_TEST_IDS.walkNoyauDiscarded}
          className="text-gray-600 text-caption"
        >
          {discarded}
        </p>
      )}
    </>
  );
}

/**
 * Another reading of a candidate, on a line: « Lecture 1 · 79 · 3 citations soutiennent, 1 contredit »,
 * a chevron before it. Unfolded it reads « Lecture 1 · 79 » and shows what the reading wrote, whole; the
 * count of its quotations goes, they are there. The fold is the one of `walk-fold.tsx`: a button with
 * `aria-expanded`, the body not mounted while folded.
 */
function ReadingFold({
  noyau,
  reading,
  open,
  onToggle,
}: {
  noyau: NoyauCode;
  reading: CandidateReadings['others'][number];
  open: boolean;
  onToggle: () => void;
}) {
  const bodyId = useId();
  return (
    <li
      data-testid={SESSIONS_TEST_IDS.walkNoyauReadingFold}
      data-noyau={noyau}
      data-run={reading.run}
      data-open={open ? 'true' : 'false'}
      className="flex flex-col gap-2.5 py-1"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        data-testid={SESSIONS_TEST_IDS.walkNoyauReadingToggle}
        onClick={onToggle}
        className="inline-flex cursor-pointer flex-wrap items-center gap-x-1.5 text-left text-caption text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
      >
        <Icon name={open ? 'expand_more' : 'chevron_right'} size={14} />
        <b className="font-semibold text-gray-900">Lecture {reading.run}</b>{' '}
        <span className="tabular-nums">
          · {reading.score}
          {open ? '' : ` · ${reading.citations}`}
        </span>
      </button>
      {open ? (
        <div id={bodyId} className="flex flex-col gap-2.5 pl-5">
          <ReadingBody note={reading.note} />
        </div>
      ) : null}
    </li>
  );
}

/** The passages of one side, word for word between guillemets; nothing at all when there are none. */
function Passages({
  label,
  side,
  passages,
}: {
  label: string;
  side: keyof typeof PASSAGE_SIDE_CLASS;
  passages: readonly string[];
}) {
  if (passages.length === 0) return null;
  return (
    <div className="flex flex-col gap-1" data-side={side}>
      <p className={cn('font-semibold text-caption', PASSAGE_SIDE_CLASS[side])}>{label}</p>
      <ul className="flex flex-col gap-0.5 text-caption text-gray-900">
        {passages.map((passage, index) => (
          // The rank is in the key: two passages of one note can be the same words.
          <li key={`${index}-${passage}`}>{`« ${passage} »`}</li>
        ))}
      </ul>
    </div>
  );
}
